// src/apiServices/authService.js
import axios from 'axios';
import { APIUserLogin, APIUserLogout, BASE_URL } from './APIs';

// Storage keys
const USER_INFO_KEY = 'userInfo';

class AuthService {
    constructor() {
        this.api = axios.create({
            baseURL: BASE_URL,
            timeout: 30000,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        });
    }

    /**
     * Login user - Admin only
     * Matches backend UserController@login
     */
    async login(email, password) {
        try {
            const response = await this.api.post(APIUserLogin, {
                email,
                password
            });

            // Backend returns: { status: 'success', message: '...', data: {...} }
            // or: { status: 'error', message: '...', errors: {...} }
            
            if (response.data.status === 'success') {
                // Store user info in sessionStorage
                const userData = response.data.data;
                if (userData) {
                    // Add role and timestamp
                    userData.role = 'admin';
                    userData.login_time = new Date().toISOString();
                    sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(userData));
                }
                
                return {
                    success: true,
                    data: userData,
                    message: response.data.message || 'Login successful'
                };
            } else {
                // Handle error responses matching backend format
                return {
                    success: false,
                    message: response.data.message || 'Login failed',
                    errors: response.data.errors || null
                };
            }
        } catch (error) {
            // Handle different error scenarios
            if (error.response) {
                // Server responded with error status
                const { status, data } = error.response;
                
                switch (status) {
                    case 401:
                        return {
                            success: false,
                            message: data.message || 'Invalid email or password',
                            errors: data.errors || null
                        };
                    case 403:
                        return {
                            success: false,
                            message: data.message || 'Account is inactive',
                            errors: data.errors || null
                        };
                    case 422:
                        return {
                            success: false,
                            message: data.message || 'Validation failed',
                            errors: data.errors || null
                        };
                    case 500:
                        return {
                            success: false,
                            message: 'Server error. Please try again later.',
                            errors: null
                        };
                    default:
                        return {
                            success: false,
                            message: data.message || 'Login failed',
                            errors: data.errors || null
                        };
                }
            } else if (error.request) {
                // Request made but no response received
                return {
                    success: false,
                    message: 'Unable to connect to server. Please check your internet connection.',
                    errors: null
                };
            } else {
                // Something happened in setting up the request
                return {
                    success: false,
                    message: 'An unexpected error occurred',
                    errors: null
                };
            }
        }
    }

    /**
     * Logout user
     * Matches backend UserController@logout
     */
    async logout() {
        try {
            const user = this.getCurrentUser();
            const response = await this.api.post(APIUserLogout, {
                user_id: user?.id
            });

            if (response.data.status === 'success') {
                console.log('Logout successful:', response.data.message);
            }
        } catch (error) {
            console.error('Logout error:', error.response?.data || error.message);
        } finally {
            this.clearUserData();
        }
    }

    /**
     * Clear user data from storage
     */
    clearUserData() {
        sessionStorage.removeItem(USER_INFO_KEY);
        // Also clear any other stored data
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('userRole');
    }

    /**
     * Get current user from storage
     */
    getCurrentUser() {
        const userInfo = sessionStorage.getItem(USER_INFO_KEY);
        if (!userInfo) return null;
        
        try {
            return JSON.parse(userInfo);
        } catch (error) {
            console.error('Error parsing user info:', error);
            this.clearUserData(); // Clear corrupted data
            return null;
        }
    }

    /**
     * Get user role
     */
    getUserRole() {
        const user = this.getCurrentUser();
        return user ? user.role : null;
    }

    /**
     * Get user ID
     */
    getUserId() {
        const user = this.getCurrentUser();
        return user ? user.id : null;
    }

    /**
     * Get user name
     */
    getUserName() {
        const user = this.getCurrentUser();
        return user ? user.name : null;
    }

    /**
     * Get user email
     */
    getUserEmail() {
        const user = this.getCurrentUser();
        return user ? user.email : null;
    }

    /**
     * Get user token (if using token-based auth in future)
     */
    getUserToken() {
        const user = this.getCurrentUser();
        return user ? user.token : null;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        const user = this.getCurrentUser();
        return !!(user && user.id && user.role === 'admin');
    }

    /**
     * Check if user is admin (only role available)
     */
    isAdmin() {
        return this.getUserRole() === 'admin';
    }

    /**
     * Check if user account is active
     */
    isActive() {
        const user = this.getCurrentUser();
        return user ? user.is_active === true || user.is_active === 1 : false;
    }

    /**
     * Get axios instance for making authenticated requests
     */
    getApi() {
        return this.api;
    }

    /**
     * Update user data in storage
     */
    updateUserData(userData) {
        try {
            const currentUser = this.getCurrentUser();
            if (currentUser) {
                const updatedUser = { ...currentUser, ...userData };
                sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(updatedUser));
            }
        } catch (error) {
            console.error('Error updating user data:', error);
        }
    }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;